/**
 * Phantom Mobile sign-in via Phantom's native deeplink protocol.
 *
 * Spec: https://docs.phantom.com/phantom-deeplinks/provider-methods
 *
 * Two round-trips for a full sign-in:
 *   1. /ul/v1/connect → user approves → Phantom redirects with an
 *      encrypted payload containing { public_key, session }.
 *   2. /ul/v1/signMessage (using the session from step 1) → user
 *      approves → Phantom redirects with an encrypted { signature }.
 *
 * Each round-trip is a deep-link out + a redirect back to a URL we
 * provide via `redirect_link`. We stash the dApp's ephemeral keypair
 * and step-2 state in localStorage (NOT sessionStorage) — iOS often
 * opens Phantom's universal-link return in a new Safari tab when the
 * originating tab was backgrounded, and sessionStorage is per-tab.
 *
 * Tradeoff vs WC v2: more round-trips, but `redirect_link` is a
 * REQUIRED parameter — Phantom *has* to honor it, so auto-return is
 * reliable. (WC's `metadata.redirect.universal` is optional, which is
 * how Jupiter Mobile silently ignores it.)
 */
import nacl from 'tweetnacl';
import bs58 from 'bs58';

const PHANTOM_BASE = 'https://phantom.app/ul/v1';

// Where Phantom is told to redirect back to. Dedicated path so a single
// route component can handle both the connect-callback and the
// signMessage-callback (it switches on what's in localStorage).
const CALLBACK_PATH = '/auth/phantom-callback';

// localStorage keys. We deliberately use localStorage instead of
// sessionStorage: iOS Safari frequently opens Phantom's universal-link
// return in a *new* tab (especially when the originating tab was
// backgrounded while the user was in Phantom). sessionStorage is
// per-tab, so we'd lose the keypair and bounce the user home with no
// idea what went wrong. localStorage survives the tab change; the
// TTL guard below keeps stale aborted flows from poisoning later
// attempts.
const SK_KEYPAIR = 'phantom_dl_keypair';        // our ephemeral x25519 keypair (base58'd)
const SK_INTENT  = 'phantom_dl_intent';         // 'connect' | 'sign'
const SK_SESSION = 'phantom_dl_session';        // Phantom's session token (after connect)
const SK_PHANTOM_PK = 'phantom_dl_phantom_pk';  // Phantom's encryption pubkey (after connect)
const SK_RETURN_URL = 'phantom_dl_return_url';  // where to send the user when done
const SK_MESSAGE = 'phantom_dl_message';        // the human-readable message we asked Phantom to sign
const SK_LINK_ANCHOR = 'phantom_dl_anchor';     // optional anchor on the return URL
const SK_STARTED_AT = 'phantom_dl_started_at';  // ms timestamp; flow is considered stale after FLOW_TTL_MS

// A full Phantom sign-in (connect + signMessage, two user approvals)
// generally completes within seconds. 10 minutes is plenty for slow
// users; anything older is almost certainly an abandoned attempt that
// shouldn't quietly take over a fresh sign-in.
const FLOW_TTL_MS = 10 * 60 * 1000;

// Persistent (cross-flow) keys: after a successful connect we keep
// these around so the next sign-in can skip Phantom's connect prompt
// and go straight to signMessage. Phantom session tokens don't have a
// documented TTL but in practice they last weeks; we cap our cache at
// 30 days to limit blast radius if a session is silently revoked
// upstream.
const PSK_KEYPAIR     = 'phantom_persist_keypair';
const PSK_SESSION     = 'phantom_persist_session';
const PSK_PHANTOM_PK  = 'phantom_persist_phantom_pk';
const PSK_WALLET_PK   = 'phantom_persist_wallet_pubkey';
const PSK_SAVED_AT    = 'phantom_persist_saved_at';
const PERSISTENT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

// Continuation keys for the signTransaction deeplink hop. Each call
// to startPhantomSignTransaction stashes these so when Phantom
// redirects back, the callback page knows which originating flow
// kicked it off (BurnCredits / SwapPanel / etc) and where to send
// the user. The actual signed tx lands in TX_RESULT_SIGNED for the
// originating page to pick up on mount.
const TX_OPERATION       = 'phantom_tx_operation';
const TX_OPERATION_PARAMS = 'phantom_tx_params';
const TX_RETURN_URL      = 'phantom_tx_return_url';
const TX_RESULT_SIGNED   = 'phantom_tx_result_signed';
const TX_RESULT_ERROR    = 'phantom_tx_result_error';
const TX_RESULT_OPERATION = 'phantom_tx_result_operation';

export interface PhantomFlowState {
  ourKeypair: nacl.BoxKeyPair;
  intent: 'connect' | 'sign';
  phantomPublicKey?: Uint8Array;
  session?: string;
  message?: string;        // base64-encoded message bytes (so we can recover the original)
  returnUrl: string;
  anchor?: string;
}

function callbackUrl(): string {
  return `${window.location.origin}${CALLBACK_PATH}`;
}

function saveKeypair(kp: nacl.BoxKeyPair): void {
  localStorage.setItem(SK_KEYPAIR, JSON.stringify({
    publicKey: bs58.encode(kp.publicKey),
    secretKey: bs58.encode(kp.secretKey),
  }));
}

function loadKeypair(): nacl.BoxKeyPair | null {
  const raw = localStorage.getItem(SK_KEYPAIR);
  if (!raw) return null;
  try {
    const { publicKey, secretKey } = JSON.parse(raw);
    return { publicKey: bs58.decode(publicKey), secretKey: bs58.decode(secretKey) };
  } catch { return null; }
}

function clearAll(): void {
  for (const k of [SK_KEYPAIR, SK_INTENT, SK_SESSION, SK_PHANTOM_PK, SK_RETURN_URL, SK_MESSAGE, SK_LINK_ANCHOR, SK_STARTED_AT, 'phantom_dl_pubkey']) {
    localStorage.removeItem(k);
  }
}

/** Returns true if the stored flow is older than FLOW_TTL_MS. */
function isFlowStale(): boolean {
  const startedAt = localStorage.getItem(SK_STARTED_AT);
  if (!startedAt) return false;
  const t = parseInt(startedAt, 10);
  if (!Number.isFinite(t)) return true;
  return Date.now() - t > FLOW_TTL_MS;
}

interface PersistentSession {
  keypair: nacl.BoxKeyPair;
  session: string;
  phantomPk: string;
  walletPubkey: string;
}

/** Returns the saved session if one exists, is non-stale, and parses cleanly. */
function loadPersistentSession(): PersistentSession | null {
  const savedAt = localStorage.getItem(PSK_SAVED_AT);
  if (!savedAt) return null;
  const t = parseInt(savedAt, 10);
  if (!Number.isFinite(t) || Date.now() - t > PERSISTENT_TTL_MS) return null;

  const kpRaw = localStorage.getItem(PSK_KEYPAIR);
  const session = localStorage.getItem(PSK_SESSION);
  const phantomPk = localStorage.getItem(PSK_PHANTOM_PK);
  const walletPubkey = localStorage.getItem(PSK_WALLET_PK);
  if (!kpRaw || !session || !phantomPk || !walletPubkey) return null;

  try {
    const { publicKey, secretKey } = JSON.parse(kpRaw);
    return {
      keypair: { publicKey: bs58.decode(publicKey), secretKey: bs58.decode(secretKey) },
      session,
      phantomPk,
      walletPubkey,
    };
  } catch {
    return null;
  }
}

/** Save the session for next time. Called once after a successful connect. */
function savePersistentSession(s: PersistentSession): void {
  localStorage.setItem(PSK_KEYPAIR, JSON.stringify({
    publicKey: bs58.encode(s.keypair.publicKey),
    secretKey: bs58.encode(s.keypair.secretKey),
  }));
  localStorage.setItem(PSK_SESSION, s.session);
  localStorage.setItem(PSK_PHANTOM_PK, s.phantomPk);
  localStorage.setItem(PSK_WALLET_PK, s.walletPubkey);
  localStorage.setItem(PSK_SAVED_AT, String(Date.now()));
}

/** Wipe persistent state — called when a saved session is rejected by Phantom. */
export function clearPersistentPhantomSession(): void {
  for (const k of [PSK_KEYPAIR, PSK_SESSION, PSK_PHANTOM_PK, PSK_WALLET_PK, PSK_SAVED_AT]) {
    localStorage.removeItem(k);
  }
}

/**
 * Pull out everything we have right now, so the callback page can
 * display diagnostic info when something goes wrong. Don't include
 * the secret key — only show it's present.
 */
export function phantomDeeplinkDebugSnapshot(): Record<string, string> {
  const startedAt = localStorage.getItem(SK_STARTED_AT);
  const ageMs = startedAt ? Date.now() - parseInt(startedAt, 10) : null;
  const persistSavedAt = localStorage.getItem(PSK_SAVED_AT);
  const persistAgeMs = persistSavedAt ? Date.now() - parseInt(persistSavedAt, 10) : null;
  return {
    intent: localStorage.getItem(SK_INTENT) || '(none)',
    keypair: localStorage.getItem(SK_KEYPAIR) ? 'present' : '(none)',
    session: localStorage.getItem(SK_SESSION) ? 'present' : '(none)',
    phantomPk: localStorage.getItem(SK_PHANTOM_PK) ? 'present' : '(none)',
    returnUrl: localStorage.getItem(SK_RETURN_URL) || '(none)',
    startedAt: startedAt || '(none)',
    ageSec: ageMs !== null ? String(Math.round(ageMs / 1000)) : '(none)',
    'persist.keypair': localStorage.getItem(PSK_KEYPAIR) ? 'present' : '(none)',
    'persist.session': localStorage.getItem(PSK_SESSION) ? 'present' : '(none)',
    'persist.phantomPk': localStorage.getItem(PSK_PHANTOM_PK) ? 'present' : '(none)',
    'persist.walletPk': localStorage.getItem(PSK_WALLET_PK) ? 'present' : '(none)',
    'persist.ageSec': persistAgeMs !== null ? String(Math.round(persistAgeMs / 1000)) : '(none)',
    href: window.location.href.slice(0, 200),
  };
}

/**
 * Kick off Phantom sign-in. If a non-stale persistent session exists
 * from a prior successful connect, skip Phantom's connect prompt
 * entirely and go straight to signMessage — collapses the two-tap
 * flow to one tap on repeat sign-ins.
 *
 * Otherwise: generate an ephemeral keypair, stash it (+ where to
 * return the user) in localStorage, then navigate to Phantom's
 * universal link. Phantom will redirect back to /auth/phantom-callback
 * with the connect response, and the second hop (signMessage) follows.
 */
export function startPhantomSignIn(opts: { returnUrl: string; anchor?: string }): void {
  const saved = loadPersistentSession();
  if (saved) {
    // Fast path: rehydrate ephemeral state from persistent and bounce
    // straight to signMessage. The callback handler already knows how
    // to drive this from intent === 'sign'.
    clearAll();
    saveKeypair(saved.keypair);
    localStorage.setItem(SK_PHANTOM_PK, saved.phantomPk);
    localStorage.setItem(SK_SESSION, saved.session);
    localStorage.setItem(SK_RETURN_URL, opts.returnUrl);
    localStorage.setItem(SK_STARTED_AT, String(Date.now()));
    if (opts.anchor) localStorage.setItem(SK_LINK_ANCHOR, opts.anchor);

    const message = `Sign in to deepnets.ai\nTimestamp: ${new Date().toISOString()}`;
    const messageBytes = new TextEncoder().encode(message);
    try {
      startPhantomSignMessage(messageBytes, saved.walletPubkey);
      return;
    } catch {
      // Fall through to fresh-connect path if the persistent state
      // turned out to be inconsistent.
      clearPersistentPhantomSession();
    }
  }

  // Slow path: full connect + signMessage flow.
  clearAll();

  const kp = nacl.box.keyPair();
  saveKeypair(kp);
  localStorage.setItem(SK_INTENT, 'connect');
  localStorage.setItem(SK_RETURN_URL, opts.returnUrl);
  localStorage.setItem(SK_STARTED_AT, String(Date.now()));
  if (opts.anchor) localStorage.setItem(SK_LINK_ANCHOR, opts.anchor);

  const params = new URLSearchParams({
    app_url: window.location.origin,
    dapp_encryption_public_key: bs58.encode(kp.publicKey),
    redirect_link: callbackUrl(),
    cluster: 'mainnet-beta',
  });
  window.location.href = `${PHANTOM_BASE}/connect?${params}`;
}

/** Quick check the picker can use to render "fast sign-in" hint. */
export function hasPersistentPhantomSession(): boolean {
  return loadPersistentSession() !== null;
}

/**
 * Wallet-scoped check: returns the cached Phantom session's wallet
 * address only when it matches `expectedAddress` (or any when null).
 * Used by the in-app transaction flows so we don't accidentally route
 * a Jupiter-WC-signed-in user through Phantom just because a stale
 * Phantom session happens to be in localStorage from a prior login.
 */
export function persistentPhantomWalletMatches(expectedAddress: string | null | undefined): boolean {
  const saved = loadPersistentSession();
  if (!saved) return false;
  if (!expectedAddress) return true;
  return saved.walletPubkey === expectedAddress;
}

/**
 * Called by the callback route on page load. Inspects the URL for
 * Phantom's response and dispatches based on which step we're on.
 *
 * Returns either the next action to take (start sign, complete login)
 * or an error explaining what went wrong.
 */
export type PhantomCallbackResult =
  | { kind: 'idle' }                                              // no state, not a callback
  | { kind: 'sign-needed'; publicKey: string }                    // connect done; caller should start sign
  | { kind: 'login-ready'; publicKey: string; message: string; signature: Uint8Array; returnUrl: string }
  | { kind: 'tx-signed'; signedTransactionBase58: string; operation: string; returnUrl: string }
  // `txReturnUrl` is set when the failed flow was a transaction signing
  // (send-tx). The callback page redirects there so the originating page
  // can surface the error in context (e.g. on the credits-purchase modal).
  | { kind: 'rejected'; txReturnUrl?: string }
  | { kind: 'error'; message: string; txReturnUrl?: string };

export function handlePhantomCallback(searchParams: URLSearchParams): PhantomCallbackResult {
  // User-rejected paths come back with errorCode/errorMessage params.
  const errorCode = searchParams.get('errorCode');
  if (errorCode) {
    const errorMessage = searchParams.get('errorMessage') || 'Cancelled in Phantom';
    const intent = localStorage.getItem(SK_INTENT);
    const isReject = errorCode === '4001' || errorMessage.toLowerCase().includes('reject');
    const operation = localStorage.getItem(TX_OPERATION);
    const txReturnUrl = localStorage.getItem(TX_RETURN_URL) || undefined;
    clearAll();
    // If Phantom rejected a sign step (not a fresh connect), the saved
    // session may have been revoked on Phantom's side — wipe it so the
    // next attempt does a fresh connect rather than silently failing
    // again. Don't wipe on user-cancel: that was a deliberate action,
    // not a session-validity issue.
    if ((intent === 'sign' || intent === 'send-tx') && !isReject) {
      clearPersistentPhantomSession();
    }
    // For a transaction round-trip we need to surface the error on
    // the originating page, not the callback. Stash it; the callback
    // page will redirect to txReturnUrl which picks the result up.
    if (intent === 'send-tx' && operation) {
      _stashPhantomTxResult(operation, null, isReject ? 'Cancelled in Phantom' : errorMessage);
    }
    return isReject
      ? { kind: 'rejected', txReturnUrl: intent === 'send-tx' ? txReturnUrl : undefined }
      : { kind: 'error', message: errorMessage, txReturnUrl: intent === 'send-tx' ? txReturnUrl : undefined };
  }

  if (isFlowStale()) {
    clearAll();
    return { kind: 'idle' };
  }

  const intent = localStorage.getItem(SK_INTENT) as 'connect' | 'sign' | 'send-tx' | null;
  const ourKp = loadKeypair();
  if (!intent || !ourKp) {
    // No flow state. If Phantom *did* hand us a payload, that means
    // localStorage got wiped between kicking off and coming back —
    // worth surfacing rather than silently dropping the user home.
    const hasPhantomPayload = searchParams.has('phantom_encryption_public_key') || searchParams.has('data');
    return hasPhantomPayload
      ? { kind: 'error', message: 'Lost the Phantom session locally — please try signing in again.' }
      : { kind: 'idle' };
  }

  if (intent === 'connect') {
    const phantomPk = searchParams.get('phantom_encryption_public_key');
    const nonce = searchParams.get('nonce');
    const data = searchParams.get('data');
    if (!phantomPk || !nonce || !data) return { kind: 'error', message: 'Missing Phantom response fields' };

    try {
      const phantomPkBytes = bs58.decode(phantomPk);
      const shared = nacl.box.before(phantomPkBytes, ourKp.secretKey);
      const decrypted = nacl.box.open.after(bs58.decode(data), bs58.decode(nonce), shared);
      if (!decrypted) return { kind: 'error', message: "Couldn't decrypt Phantom response" };
      const payload = JSON.parse(new TextDecoder().decode(decrypted)) as { public_key: string; session: string };

      localStorage.setItem(SK_PHANTOM_PK, phantomPk);
      localStorage.setItem(SK_SESSION, payload.session);

      // Persist for next time so we can skip the connect bounce.
      // Stays valid until PERSISTENT_TTL_MS, an error during a future
      // sign step (above), or explicit clearPersistentPhantomSession().
      savePersistentSession({
        keypair: ourKp,
        session: payload.session,
        phantomPk,
        walletPubkey: payload.public_key,
      });

      return { kind: 'sign-needed', publicKey: payload.public_key };
    } catch (e: any) {
      return { kind: 'error', message: e?.message || 'Decryption failed' };
    }
  }

  if (intent === 'send-tx') {
    const phantomPk = localStorage.getItem(SK_PHANTOM_PK);
    const operation = localStorage.getItem(TX_OPERATION);
    const returnUrl = localStorage.getItem(TX_RETURN_URL) ?? window.location.origin;
    const nonce = searchParams.get('nonce');
    const data = searchParams.get('data');
    if (!phantomPk || !operation || !nonce || !data) {
      return { kind: 'error', message: 'Missing Phantom signTransaction response state' };
    }

    try {
      const phantomPkBytes = bs58.decode(phantomPk);
      const shared = nacl.box.before(phantomPkBytes, ourKp.secretKey);
      const decrypted = nacl.box.open.after(bs58.decode(data), bs58.decode(nonce), shared);
      if (!decrypted) return { kind: 'error', message: "Couldn't decrypt signTransaction response" };
      const payload = JSON.parse(new TextDecoder().decode(decrypted)) as { transaction: string };

      _stashPhantomTxResult(operation, payload.transaction, null);
      // Don't clearAll yet — the PhantomCallback page reads operation
      // for routing. It clears via consumePhantomTxResult on the
      // originating page.
      return {
        kind: 'tx-signed',
        signedTransactionBase58: payload.transaction,
        operation,
        returnUrl,
      };
    } catch (e: any) {
      return { kind: 'error', message: e?.message || 'Decryption failed' };
    }
  }

  // intent === 'sign'
  const phantomPk = localStorage.getItem(SK_PHANTOM_PK);
  const messageBase64 = localStorage.getItem(SK_MESSAGE);
  const returnUrl = localStorage.getItem(SK_RETURN_URL) ?? window.location.origin;
  const nonce = searchParams.get('nonce');
  const data = searchParams.get('data');
  if (!phantomPk || !messageBase64 || !nonce || !data) {
    return { kind: 'error', message: 'Missing Phantom signMessage response state' };
  }

  try {
    const phantomPkBytes = bs58.decode(phantomPk);
    const shared = nacl.box.before(phantomPkBytes, ourKp.secretKey);
    const decrypted = nacl.box.open.after(bs58.decode(data), bs58.decode(nonce), shared);
    if (!decrypted) return { kind: 'error', message: "Couldn't decrypt signMessage response" };
    const payload = JSON.parse(new TextDecoder().decode(decrypted)) as { signature: string };

    // Reconstruct the message bytes from base64.
    const messageBytes = Uint8Array.from(atob(messageBase64), c => c.charCodeAt(0));
    const message = new TextDecoder().decode(messageBytes);
    const signature = bs58.decode(payload.signature);
    const publicKey = localStorage.getItem('phantom_dl_pubkey') ?? '';

    clearAll();
    return { kind: 'login-ready', publicKey, message, signature, returnUrl };
  } catch (e: any) {
    return { kind: 'error', message: e?.message || 'Decryption failed' };
  }
}

/**
 * Step 2: encrypt the message we want signed and bounce to Phantom.
 * Called from the callback route after the connect step has populated
 * session + phantomPublicKey in localStorage.
 */
export function startPhantomSignMessage(messageBytes: Uint8Array, publicKey: string): void {
  const ourKp = loadKeypair();
  const phantomPk = localStorage.getItem(SK_PHANTOM_PK);
  const session = localStorage.getItem(SK_SESSION);
  if (!ourKp || !phantomPk || !session) {
    throw new Error('Phantom session not initialized — connect first');
  }

  // Stash the message + pubkey so the second callback can decrypt the
  // signature and reconstruct the original bytes.
  localStorage.setItem(SK_INTENT, 'sign');
  localStorage.setItem(SK_MESSAGE, btoa(String.fromCharCode(...messageBytes)));
  localStorage.setItem('phantom_dl_pubkey', publicKey);

  const phantomPkBytes = bs58.decode(phantomPk);
  const shared = nacl.box.before(phantomPkBytes, ourKp.secretKey);
  const payload = {
    message: bs58.encode(messageBytes),
    session,
    display: 'utf8' as const,
  };
  const nonce = nacl.randomBytes(24);
  const ciphertext = nacl.box.after(new TextEncoder().encode(JSON.stringify(payload)), nonce, shared);

  const params = new URLSearchParams({
    dapp_encryption_public_key: bs58.encode(ourKp.publicKey),
    nonce: bs58.encode(nonce),
    redirect_link: callbackUrl(),
    payload: bs58.encode(ciphertext),
  });
  window.location.href = `${PHANTOM_BASE}/signMessage?${params}`;
}

// ─── Transaction signing via deeplink ────────────────────────────────────
//
// Same crypto + bounce shape as signMessage, but for unsigned Solana
// transactions. Used by the in-app credit/swap/subscription flows so a
// mobile-Phantom-signed-in user can complete a purchase without going
// back through wallet-adapter / picker / re-connect dance.
//
// Asynchronous-by-redirect: we navigate away to Phantom, the React tree
// is torn down, and the originating page restarts cold when Phantom
// redirects to /auth/phantom-callback. The callback page surfaces the
// signed tx via TX_RESULT_SIGNED in localStorage and bounces the user
// back to opts.returnUrl with `?phantom-tx-pending=1`. The originating
// page picks the result up via consumePhantomTxResult() on mount.

export interface SignTransactionOpts {
  /** Base58-encoded serialized transaction (unsigned). Match Phantom's
   *  signTransaction spec — they decode bs58 and treat as a Solana tx. */
  transactionBase58: string;
  /** Free-form label for the originating flow (e.g. 'burn-credits',
   *  'buy-credits', 'swap'). Used by the originating page to know what
   *  to do with the returned signed tx. */
  operation: string;
  /** Operation-specific data the originating page needs after the bounce. */
  params?: Record<string, unknown>;
  /** Where to send the user when Phantom returns. Should be the page
   *  that initiated the sign so its on-mount continuation can run. */
  returnUrl: string;
}

/**
 * Encrypt an unsigned tx with the cached Phantom session and bounce to
 * Phantom. Throws synchronously if no persistent session exists —
 * caller should fall back to the wallet-adapter path or prompt sign-in.
 */
export function startPhantomSignTransaction(opts: SignTransactionOpts): void {
  const saved = loadPersistentSession();
  if (!saved) {
    throw new Error('No persistent Phantom session — sign in first');
  }

  // Stash continuation context for the callback + originating page.
  localStorage.setItem(TX_OPERATION, opts.operation);
  localStorage.setItem(TX_OPERATION_PARAMS, JSON.stringify(opts.params || {}));
  localStorage.setItem(TX_RETURN_URL, opts.returnUrl);

  // Set ephemeral state. The callback flow shares the same keypair +
  // session as a regular sign-message round-trip, distinguished by
  // intent='send-tx'.
  clearAll();
  saveKeypair(saved.keypair);
  localStorage.setItem(SK_PHANTOM_PK, saved.phantomPk);
  localStorage.setItem(SK_SESSION, saved.session);
  localStorage.setItem(SK_INTENT, 'send-tx');
  localStorage.setItem(SK_RETURN_URL, opts.returnUrl);
  localStorage.setItem(SK_STARTED_AT, String(Date.now()));

  const phantomPkBytes = bs58.decode(saved.phantomPk);
  const shared = nacl.box.before(phantomPkBytes, saved.keypair.secretKey);
  const payload = {
    transaction: opts.transactionBase58,
    session: saved.session,
  };
  const nonce = nacl.randomBytes(24);
  const ciphertext = nacl.box.after(new TextEncoder().encode(JSON.stringify(payload)), nonce, shared);

  const params = new URLSearchParams({
    dapp_encryption_public_key: bs58.encode(saved.keypair.publicKey),
    nonce: bs58.encode(nonce),
    redirect_link: callbackUrl(),
    payload: bs58.encode(ciphertext),
  });
  window.location.href = `${PHANTOM_BASE}/signTransaction?${params}`;
}

/**
 * Result an originating page picks up after the deeplink-tx round-trip.
 * `signedTransactionBase58` is what Phantom returned: the same tx shape
 * we sent, now signed. Caller is responsible for sending it to the
 * network (gives us preflight/retry control vs signAndSendTransaction).
 */
export interface PhantomTxResult {
  operation: string;
  params: Record<string, unknown>;
  signedTransactionBase58?: string;
  error?: string;
}

/**
 * Read + clear the result of the most recent signTransaction round-trip.
 * Called on mount of any page that uses the deeplink-tx flow.
 *
 * @param expectedOperation - Only consume if the stashed operation
 *   matches this label. Required because the credits page mounts both
 *   BurnCredits and CryptoBuyCredits side-by-side; the first to call
 *   would otherwise clear the result the second one needed. Pass
 *   `null` to consume regardless of operation (only safe when no
 *   sibling component is also listening).
 */
export function consumePhantomTxResult(expectedOperation: string | null = null): PhantomTxResult | null {
  const operation = localStorage.getItem(TX_RESULT_OPERATION);
  if (!operation) return null;
  if (expectedOperation !== null && operation !== expectedOperation) {
    // Not for us — leave state untouched so the right component picks
    // it up on its mount.
    return null;
  }

  const signed = localStorage.getItem(TX_RESULT_SIGNED);
  const error = localStorage.getItem(TX_RESULT_ERROR);
  let params: Record<string, unknown> = {};
  try {
    params = JSON.parse(localStorage.getItem(TX_OPERATION_PARAMS) || '{}');
  } catch { /* leave empty */ }

  // Clear all the continuation state so refreshes don't re-fire.
  for (const k of [TX_OPERATION, TX_OPERATION_PARAMS, TX_RETURN_URL, TX_RESULT_SIGNED, TX_RESULT_ERROR, TX_RESULT_OPERATION]) {
    localStorage.removeItem(k);
  }

  return {
    operation,
    params,
    signedTransactionBase58: signed || undefined,
    error: error || undefined,
  };
}

/** Internal — used by handlePhantomCallback to hand a signed tx off. */
export function _stashPhantomTxResult(operation: string, signedTxBase58: string | null, error: string | null): void {
  localStorage.setItem(TX_RESULT_OPERATION, operation);
  if (signedTxBase58) localStorage.setItem(TX_RESULT_SIGNED, signedTxBase58);
  if (error) localStorage.setItem(TX_RESULT_ERROR, error);
}

/**
 * Read-only peek at the pending tx operation without consuming.
 * Useful for parent components (e.g. CreditsPage) that need to know
 * which child tab to mount so that child's consumePhantomTxResult
 * call actually runs.
 */
export function peekPendingPhantomTxOperation(): string | null {
  return localStorage.getItem(TX_RESULT_OPERATION);
}
